import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Save, Loader2, FileText, Trophy, Target, Wallet, Settings2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailSettingsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/email-settings`, { headers: getAuthHeaders() });
      setSettings(res.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/email-settings`, settings, { headers: getAuthHeaders() });
      setSettings(res.data);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center h-64"><div className="spinner w-10 h-10"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="email-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="w-8 h-8 text-blue-600" />
            Configuración de Reportes
          </h1>
          <p className="text-slate-500 mt-1">Personaliza el contenido de los reportes mensuales por email</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="save-email-settings">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      {/* Content Sections Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-slate-600" />
            Secciones del Reporte
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium text-slate-900">Resumen de Ventas</p>
                <p className="text-sm text-slate-500">Total vendido y cantidad de pasajes</p>
              </div>
            </div>
            <Switch
              checked={settings.include_sales_summary}
              onCheckedChange={(v) => setSettings({ ...settings, include_sales_summary: v })}
              data-testid="toggle-sales-summary"
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="font-medium text-slate-900">Tabla de Clasificación</p>
                <p className="text-sm text-slate-500">Posición del gestor en el ranking</p>
              </div>
            </div>
            <Switch
              checked={settings.include_leaderboard}
              onCheckedChange={(v) => setSettings({ ...settings, include_leaderboard: v })}
              data-testid="toggle-leaderboard"
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-slate-900">Progreso de Metas</p>
                <p className="text-sm text-slate-500">Avance hacia las metas asignadas</p>
              </div>
            </div>
            <Switch
              checked={settings.include_goals_progress}
              onCheckedChange={(v) => setSettings({ ...settings, include_goals_progress: v })}
              data-testid="toggle-goals"
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div className="flex items-center gap-3">
              <Wallet className="w-5 h-5 text-purple-500" />
              <div>
                <p className="font-medium text-slate-900">Balance de Wallet</p>
                <p className="text-sm text-slate-500">Saldo actual del gestor</p>
              </div>
            </div>
            <Switch
              checked={settings.include_wallet_balance}
              onCheckedChange={(v) => setSettings({ ...settings, include_wallet_balance: v })}
              data-testid="toggle-wallet"
            />
          </div>
        </CardContent>
      </Card>

      {/* Custom Text */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            Texto Personalizado
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Encabezado del Email</Label>
            <Textarea
              value={settings.custom_header}
              onChange={(e) => setSettings({ ...settings, custom_header: e.target.value })}
              placeholder="Ej: Felicidades por otro mes de éxito..."
              rows={3}
              className="bg-slate-50"
              data-testid="custom-header-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Pie del Email</Label>
            <Textarea
              value={settings.custom_footer}
              onChange={(e) => setSettings({ ...settings, custom_footer: e.target.value })}
              placeholder="Ej: Recuerda que las comisiones se pagan el día 15..."
              rows={3}
              className="bg-slate-50"
              data-testid="custom-footer-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettingsPage;
